/**
 * Playback engine selection and the transmux trigger.
 *
 * Extracted from `WatchPage` so it can be tested: both decisions are pure, and
 * both are load-bearing in a way that fails quietly rather than loudly.
 *
 *  - Pick the wrong engine and playback breaks in a confusing way — mpegts.js
 *    on a VOD source engages its startup-stall-jumper, which is right for a
 *    live edge and wrong for a file.
 *  - Trigger the transmux path unnecessarily and the server spends CPU and
 *    disk on a stream the browser could have played directly.
 */

export type PlaybackKind = "live" | "vod" | "episode" | "catchup";
export type PlaybackEngine = "ts" | "hls" | "native";

/** Lowercased file extension of a URL, ignoring any query string. */
export function extFromUrl(u: string): string {
  const last = u.split("?")[0].split("/").pop() || "";
  const m = /\.([a-zA-Z0-9]+)$/.exec(last);
  return (m ? m[1] : "").toLowerCase();
}

export function pickEngine(
  kind: PlaybackKind,
  container: string | null | undefined,
  url: string,
): PlaybackEngine {
  // Live and catchup are MPEG-TS in this environment.
  if (kind === "live" || kind === "catchup") return "ts";
  // VOD and episodes are never mpegts.js — even when a URL happens to end in
  // `.ts` (a transmux segment URL leaking through here is the realistic way
  // that happens), because the stall-jumper would misbehave on a file.
  const ext = (container || extFromUrl(url) || "").toLowerCase();
  if (ext === "m3u8") return "hls";
  return "native";
}

/**
 * Whether the container alone forces the server-side transmux path.
 *
 * Browsers cannot open these at any codec, so this is decided before any probe.
 * Live and catchup never qualify: they are already MPEG-TS.
 */
export function containerNeedsTranscode(
  kind: PlaybackKind,
  container: string | null | undefined,
): boolean {
  if (kind === "live" || kind === "catchup") return false;
  const ext = (container || "").toLowerCase();
  return ext === "mkv" || ext === "avi" || ext === "wmv" || ext === "mov";
}
